import { gql } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  Check,
  CircleDot,
  Hourglass,
  Mail,
  MoveDiagonalIcon,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import { toast } from 'sonner';

import {
  GET_ALLOCATION_HISTORY,
  type GetAllocationHistoryResponse
} from '../allocations/graphql';
import Header from '../Header';
import { GET_DEAL_OPPORTUNITY_IC_FUND_RESULT } from '../icFundResult';
import type {
  GetDealOpportunityIcFundResult,
  IcFundResult,
  IcFundResultItem
} from '../icFundResult';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';

import { LaunchFinalICVoteDialog } from './LaunchFinalICVoteDialog';

import type { AdditionalBreadcrumbItem } from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useOdooAuth } from '@/contexts/OdooAuthContext';

const GET_VOTES_FOR_DEAL = gql`
  query GetVotesForDeal($dealId: ID!) {
    dealIcVotes(dealId: $dealId) {
      id
      decision
      status
      voteTimestamp
      source
      changedBy {
        name
      }
      user {
        id
        name
      }
      funds {
        id
        name
      }
    }
  }
`;

const ADMIN_CHANGE_VOTE = gql`
  mutation AdminChangeVote($voteId: ID!, $decision: String!) {
    adminChangeVote(voteId: $voteId, decision: $decision) {
      ok
      message
      vote {
        id
        decision
        status
        voteTimestamp
        user {
          id
          name
        }
        funds {
          id
          name
        }
      }
    }
  }
`;

const GET_OPPORTUNITY_PARTICIPATING_COMMITTEES = gql`
  query GetOpportunityParticipatingCommittees($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        participatingCommittees {
          id
          name
          description
          isActive
          threshold
          votingOptions
          stageMapping
          fundId {
            id
            name
          }
          memberships {
            id
            userId {
              id
              name
            }
            role
            startDate
            endDate
          }
        }
      }
    }
  }
`;

interface AdminChangeVoteData {
  adminChangeVote: {
    message: null | string;
    ok: boolean;
    vote: Vote;
  };
}

interface AdminChangeVoteData {
  adminChangeVote: {
    message: null | string;
    ok: boolean;
    vote: Vote;
  };
}

interface AdminChangeVoteVariables {
  decision: string;
  voteId: string;
}

interface GetOpportunityParticipatingCommittees {
  node: {
    __typename: 'OpportunityNode';
    id: string;
    name: string;
    participatingCommittees: ParticipatingCommittee[];
  };
}

interface GetVotesForDealData {
  dealIcVotes: Vote[];
}

interface GetVotesForDealVariables {
  dealId: string;
}

interface ParticipatingCommittee {
  description: null | string;
  fundId: {
    id: string;
    name: string;
  } | null;
  id: string;
  isActive: boolean;
  memberships: {
    endDate: null | string;
    id: string;
    role: string;
    startDate: null | string;
    userId: {
      id: string;
      name: string;
    } | null;
  }[];
  name: string;
  stageMapping: null | string;
  threshold: number;
  votingOptions: null | string;
}

interface Vote {
  changedBy: {
    name: string;
  } | null;
  decision: string;
  funds: {
    id: string;
    name: string;
  }[];
  id: string;
  source: string;
  status: string;
  user: {
    id: string;
    name: string;
  } | null;
  voteTimestamp: string;
}

const VOTE_DECISIONS = [
  { label: 'Approve', value: 'approve' },
  { label: 'Reject', value: 'reject' },
  { label: 'Abstain', value: 'abstain' }
] as const;

const VotesTable = ({
  loading,
  onVoteChanged,
  votes
}: {
  loading: boolean;
  onVoteChanged?: () => void;
  votes: Vote[];
}) => {
  const { role } = useOdooAuth();
  const isAdmin = role === 'admin';
  const [changeVotePopoverId, setChangeVotePopoverId] = useState<null | string>(
    null
  );
  const [adminChangeVote, { loading: changingVote }] = useMutation<
    AdminChangeVoteData,
    AdminChangeVoteVariables
  >(ADMIN_CHANGE_VOTE, {
    onCompleted: data => {
      if (data.adminChangeVote.ok) {
        setChangeVotePopoverId(null);
        onVoteChanged?.();
        toast.success('Vote updated');
      } else {
        toast.error(data.adminChangeVote.message ?? 'Failed to change vote');
      }
    },
    onError: err => {
      toast.error(err.message || 'Failed to change vote');
    }
  });

  const handleChangeVote = (voteId: string, decision: string) => {
    void adminChangeVote({
      variables: { decision, voteId }
    });
  };
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${String(year)}-${month}-${day}, ${hours}:${minutes}`;
  };

  const getVoteDecisionBadge = (decision: string, status: string) => {
    const decisionUpper = (decision || '').toUpperCase();
    const statusUpper = (status || '').toUpperCase();

    if (statusUpper === 'PENDING') {
      return (
        <Badge className="border-transparent bg-amber-200 font-normal text-amber-800">
          <Hourglass className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
    }

    switch (decisionUpper) {
      case 'ABSTAIN':
        return (
          <Badge className="border-transparent bg-gray-200 font-normal text-gray-800">
            <CircleDot className="mr-1 h-3 w-3" />
            Abstain
          </Badge>
        );
      case 'APPROVE':
      case 'APPROVED':
        return (
          <Badge className="border-transparent bg-green-200 font-normal text-green-800">
            <Check className="mr-1 h-3 w-3" />
            Approve
          </Badge>
        );
      case 'REJECT':
      case 'REJECTED':
        return (
          <Badge className="border-transparent bg-red-200 font-normal text-red-800">
            <X className="mr-1 h-3 w-3" />
            Reject
          </Badge>
        );
      default:
        return (
          <Badge className="border-transparent bg-amber-200 font-normal text-amber-800">
            <Hourglass className="mr-1 h-3 w-3" />
            Pending
          </Badge>
        );
    }
  };

  const getSourceBadge = (vote: Vote) => {
    const { changedBy, source } = vote;
    if (source === 'admin' && changedBy != null) {
      return (
        <Badge className="border-transparent bg-blue-200 font-normal text-blue-800">
          Admin Override: {changedBy.name}
        </Badge>
      );
    }
    return (
      <Badge className="border-transparent bg-blue-200 font-normal text-blue-800">
        <Mail className="mr-1 h-3 w-3" />
        {source || 'Email'}
      </Badge>
    );
  };

  const getMemberName = (vote: Vote) => {
    if (vote.user) {
      return vote.user.name;
    }
    return 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (votes.length === 0) {
    return (
      <div className="text-muted-foreground flex min-h-[200px] items-center justify-center">
        No votes found
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Member Name</TableHead>
          <TableHead>Vote Metrics</TableHead>
          <TableHead>Timestamp</TableHead>
          <TableHead>Source</TableHead>
          {isAdmin && <TableHead>Action</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {votes.map(vote => (
          <TableRow key={vote.id}>
            <TableCell className="font-medium">{getMemberName(vote)}</TableCell>
            <TableCell>
              {getVoteDecisionBadge(vote.decision, vote.status)}
            </TableCell>
            <TableCell>
              {vote.voteTimestamp ? formatTimestamp(vote.voteTimestamp) : '-'}
            </TableCell>
            <TableCell>{getSourceBadge(vote)}</TableCell>
            {isAdmin && (
              <TableCell>
                <Popover
                  onOpenChange={open => {
                    setChangeVotePopoverId(open ? vote.id : null);
                  }}
                  open={changeVotePopoverId === vote.id}
                >
                  <PopoverTrigger asChild>
                    <Button
                      disabled={changingVote}
                      size="sm"
                      variant="outline"
                    >
                      Change Vote
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="w-40 p-2"
                  >
                    <div className="flex flex-col gap-1">
                      {VOTE_DECISIONS.map(({ label, value }) => (
                        <Button
                          disabled={changingVote}
                          key={value}
                          onClick={() => {
                            handleChangeVote(vote.id, value);
                          }}
                          size="sm"
                          variant="ghost"
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

type TableRow =
  | {
      committee_name: string;
      description: null | string;
      fund_name: string;
      id: string;
      memberships: ParticipatingCommittee['memberships'];
      status: string;
      threshold: number;
    }
  | ({ id: string } & IcFundResultItem);

const IcVotingTable = ({
  addBreadcrumb,
  additionalBreadcrumbs,
  committees,
  data,
  dealId,
  onVoteChangedRefetch,
  showVoteMetrics = true
}: {
  addBreadcrumb?: (item: AdditionalBreadcrumbItem) => void;
  additionalBreadcrumbs: AdditionalBreadcrumbItem[];
  committees?: ParticipatingCommittee[];
  data?: IcFundResult;
  dealId: string;
  onVoteChangedRefetch?: () => void;
  showVoteMetrics?: boolean;
}) => {
  const [openPopoverId, setOpenPopoverId] = useState<null | string>(null);
  const [expandedRowId, setExpandedRowId] = useState<null | string>(null);
  const [getVotes, { data: votesData, loading: votesLoading }] = useLazyQuery<
    GetVotesForDealData,
    GetVotesForDealVariables
  >(GET_VOTES_FOR_DEAL);

  const getThresholdMet = (item: TableRow) => {
    if ('approve_count' in item) {
      return item.approve_count >= item.threshold;
    }
    return false;
  };

  const getStatusBadge = (status: string) => {
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case 'FAILED':
        return (
          <Badge
            className="font-normal"
            variant="pastel-red"
          >
            Failed
          </Badge>
        );
      case 'PASSED':
        return (
          <Badge
            className="font-normal"
            variant="pastel-green"
          >
            Passed
          </Badge>
        );
      case 'PENDING':
        return (
          <Badge
            className="font-normal"
            variant="yellow"
          >
            In Progress
          </Badge>
        );
      default:
        return (
          <Badge
            className="font-normal"
            variant="pastel-slate"
          >
            Not Started
          </Badge>
        );
    }
  };

  const getVoteMetricsBar = (item: TableRow) => {
    if (!('total_votes' in item)) {
      return (
        <div className="flex h-6 w-full items-center gap-2">
          <div className="h-2 flex-1 rounded bg-gray-200" />
        </div>
      );
    }
    const total = item.total_votes || 0;
    if (total === 0) {
      return (
        <div className="flex h-6 w-full items-center gap-2">
          <div className="h-2 flex-1 rounded bg-gray-200" />
        </div>
      );
    }

    const yesPercent = (item.approve_count / total) * 100;
    const noPercent = (item.reject_count / total) * 100;
    const abstainPercent = (item.abstain_count / total) * 100;
    const notVotedPercent = (item.pending_count / total) * 100;

    return (
      <div className="flex h-6 w-full items-center gap-2">
        <div className="flex h-2 flex-1 items-center overflow-hidden rounded">
          {yesPercent > 0 && (
            <div
              className="h-full bg-blue-600"
              style={{ width: `${String(yesPercent)}%` }}
            />
          )}
          {noPercent > 0 && (
            <div
              className="h-full bg-blue-400"
              style={{ width: `${String(noPercent)}%` }}
            />
          )}
          {abstainPercent > 0 && (
            <div
              className="h-full bg-gray-300"
              style={{ width: `${String(abstainPercent)}%` }}
            />
          )}
          {notVotedPercent > 0 && (
            <div
              className="h-full bg-gray-200"
              style={{ width: `${String(notVotedPercent)}%` }}
            />
          )}
        </div>
      </div>
    );
  };

  const getVoteMetricsPopup = (item: TableRow) => {
    if (!('approve_count' in item)) return null;
    return (
      <div className="space-y-2">
        <h4 className="font-semibold">Votes per committees</h4>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-blue-600" />
            <span className="text-sm">Yes: {item.approve_count}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-blue-400" />
            <span className="text-sm">No: {item.reject_count}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-gray-300" />
            <span className="text-sm">Abstain: {item.abstain_count}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-gray-200" />
            <span className="text-sm">Not voted: {item.pending_count}</span>
          </div>
        </div>
      </div>
    );
  };

  const rows: TableRow[] = data
    ? Object.entries(data).map(([key, item]) => ({
        ...item,
        id: key
      }))
    : (committees?.map(committee => ({
        committee_name: committee.name,
        description: committee.description,
        fund_name: committee.fundId?.name ?? 'N/A',
        id: committee.id,
        memberships: committee.memberships,
        status: committee.isActive ? 'ACTIVE' : 'INACTIVE',
        threshold: committee.threshold
      })) ?? []);

  const handleViewVotes = (rowId: string, committeeName: string) => {
    if (addBreadcrumb) {
      addBreadcrumb({
        label: committeeName,
        to: `/investments/direct/${dealId}/ic-voting`
      });
    }
    if (expandedRowId === rowId) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(rowId);
      void getVotes({
        variables: { dealId }
      });
    }
  };

  const filteredVotes =
    expandedRowId && votesData?.dealIcVotes
      ? votesData.dealIcVotes.filter(vote => {
          const row = rows.find(r => r.id === expandedRowId);
          if (!row) return false;
          // Filter votes by fund_id if available
          return vote.funds.some(fund => {
            // Try to match by fund name or id
            return fund.name === row.fund_name;
          });
        })
      : [];

  useEffect(() => {
    if (!additionalBreadcrumbs.length) {
      setExpandedRowId(null);
    }
  }, [additionalBreadcrumbs]);

  if (rows.length === 0) {
    return (
      <div className="text-muted-foreground flex min-h-[400px] items-center justify-center">
        {committees ? 'No committees found' : 'No voting data available'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {expandedRowId && additionalBreadcrumbs.length > 0 && showVoteMetrics ? (
        <VotesTable
          loading={votesLoading}
          onVoteChanged={() => {
            void getVotes({ variables: { dealId } });
            onVoteChangedRefetch?.();
          }}
          votes={filteredVotes}
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {showVoteMetrics && <TableHead className="w-0" />}
              <TableHead>Committee Name</TableHead>
              <TableHead>Fund</TableHead>
              <TableHead>Threshold</TableHead>
              {showVoteMetrics && (
                <>
                  <TableHead>Threshold Met</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Vote Metrics</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <React.Fragment key={row.id}>
                <TableRow>
                  {showVoteMetrics && (
                    <TableCell className="w-0 font-medium">
                      <Button
                        onClick={() => {
                          handleViewVotes(row.id, row.committee_name);
                        }}
                        size="icon"
                        variant="ghost"
                      >
                        <MoveDiagonalIcon />
                      </Button>
                    </TableCell>
                  )}
                  <TableCell>{row.committee_name}</TableCell>
                  <TableCell>{row.fund_name}</TableCell>
                  <TableCell>{row.threshold} Required</TableCell>
                  {showVoteMetrics && (
                    <>
                      <TableCell>
                        {getThresholdMet(row) ? (
                          <Badge
                            className="font-normal"
                            variant="pastel-green"
                          >
                            Yes
                          </Badge>
                        ) : (
                          <Badge
                            className="font-normal"
                            variant="pastel-red"
                          >
                            No
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(row.status)}</TableCell>
                      <TableCell>
                        <Popover
                          onOpenChange={open => {
                            setOpenPopoverId(open ? row.id : null);
                          }}
                          open={openPopoverId === row.id}
                        >
                          <PopoverTrigger asChild>
                            <div
                              onMouseEnter={() => {
                                setOpenPopoverId(row.id);
                              }}
                              onMouseLeave={() => {
                                setOpenPopoverId(null);
                              }}
                            >
                              {getVoteMetricsBar(row)}
                            </div>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-56"
                            onMouseEnter={() => {
                              setOpenPopoverId(row.id);
                            }}
                            onMouseLeave={() => {
                              setOpenPopoverId(null);
                            }}
                          >
                            {getVoteMetricsPopup(row)}
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

const InvestmentsDirectIdIcVoting = () => {
  const { addBreadcrumb, additionalBreadcrumbs, ddStage, icVoteTimestamp, id } =
    useOutletContext<{
      addBreadcrumb?: (item: AdditionalBreadcrumbItem) => void;
      additionalBreadcrumbs: AdditionalBreadcrumbItem[];
      ddStage?: null | string;
      icVoteTimestamp?: null | string;
      id: string;
    }>();

  const {
    data,
    loading,
    refetch: refetchIcFundResult
  } = useQuery<GetDealOpportunityIcFundResult, { id: string }>(
    GET_DEAL_OPPORTUNITY_IC_FUND_RESULT,
    {
      fetchPolicy: 'no-cache',
      skip: !icVoteTimestamp || !id,
      variables: { id }
    }
  );

  const {
    data: committeesData,
    loading: committeesLoading,
    refetch: refetchCommittees
  } = useQuery<GetOpportunityParticipatingCommittees, { id: string }>(
    GET_OPPORTUNITY_PARTICIPATING_COMMITTEES,
    {
      skip: !!icVoteTimestamp || !id,
      variables: { id }
    }
  );

  const decodedOpportunityId = id ? atob(id).split(':')[1] : null;
  const { data: allocationsData, loading: allocationsLoading } = useQuery<
    GetAllocationHistoryResponse,
    { opportunityId: string }
  >(GET_ALLOCATION_HISTORY, {
    skip: !!icVoteTimestamp || !decodedOpportunityId,
    variables: { opportunityId: decodedOpportunityId ?? '' }
  });

  const allocations =
    allocationsData?.allocationHistory.filter(
      allocation => allocation.status === 'Finalized'
    ) ?? [];
  const hasAllocations = allocations.length > 0;
  return (
    <>
      <Header>
        {!icVoteTimestamp && (
          <LaunchFinalICVoteDialog
            committees={committeesData?.node.participatingCommittees}
            dealId={id}
            disabled={
              ddStage !== 'Legal, Tax, ESG' ||
              !(
                hasAllocations &&
                committeesData?.node.participatingCommittees?.length !== 0
              )
            }
            onCommitteeCreated={() => {
              setTimeout(() => {
                void refetchCommittees();
                void refetchIcFundResult();
              }, 1000);
            }}
          />
        )}
      </Header>
      <RightSidebarWrapper>
        <div className="min-w-0 space-y-2.5 px-5 pt-5 pb-16.5 md:pl-14">
          <div className="bg-card rounded-md border p-5">
            {(() => {
              const isLoading = icVoteTimestamp
                ? loading && !data?.node.icFundResult
                : committeesLoading &&
                  !committeesData?.node.participatingCommittees.length;
              if (isLoading) {
                return (
                  <div className="flex min-h-[400px] items-center justify-center">
                    <Spinner />
                  </div>
                );
              }
              if (icVoteTimestamp) {
                if (!data?.node.icFundResult) {
                  return (
                    <div className="text-muted-foreground flex min-h-[400px] items-center justify-center">
                      No voting data available
                    </div>
                  );
                }
                return (
                  <IcVotingTable
                    addBreadcrumb={addBreadcrumb}
                    additionalBreadcrumbs={additionalBreadcrumbs}
                    data={data.node.icFundResult}
                    dealId={id}
                    onVoteChangedRefetch={() => {
                      void refetchIcFundResult();
                    }}
                    showVoteMetrics={true}
                  />
                );
              }

              if (committeesData?.node.participatingCommittees.length) {
                return (
                  <IcVotingTable
                    addBreadcrumb={addBreadcrumb}
                    additionalBreadcrumbs={additionalBreadcrumbs}
                    committees={committeesData.node.participatingCommittees}
                    dealId={id}
                    showVoteMetrics={false}
                  />
                );
              }

              if (allocationsLoading) {
                return (
                  <div className="flex min-h-[400px] items-center justify-center">
                    <Spinner />
                  </div>
                );
              }

              return (
                <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <h3 className="text-xl font-semibold">
                      {hasAllocations
                        ? 'No committees set up for allocated funds'
                        : 'No allocations yet'}
                    </h3>
                    <p className="text-muted-foreground max-w-md text-sm">
                      {hasAllocations
                        ? 'No committee for the funds related to the allocations has been set up yet. Set up committees for the allocated funds to participate in the vote.'
                        : 'Awaiting allocation to determine the committees participating in the vote.'}
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdIcVoting;
